import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, Phone,
  Maximize2, Minimize2, UserPlus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' }
  ]
};

// Small inline component for user invite list
const InviteUserList = ({ currentUserId, existingPeerIds, onInvite }) => {
  const [users, setUsers] = React.useState([]);
  React.useEffect(() => {
    axios.get(`${API}/users`).then(res => {
      const exclude = new Set([currentUserId, ...(existingPeerIds || [])]);
      setUsers((res.data.users || []).filter(u => !exclude.has(u.user_id)));
    }).catch(() => {});
  }, [currentUserId, existingPeerIds]);

  return (
    <div className="space-y-0.5">
      {users.map(u => (
        <button
          key={u.user_id}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-accent transition-colors"
          onClick={() => onInvite(u.user_id)}
          data-testid={`invite-user-${u.user_id}`}
        >
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-semibold">
            {u.display_name?.[0] || u.username?.[0] || '?'}
          </div>
          <span className="flex-1 text-left truncate">{u.display_name || u.username}</span>
          <UserPlus className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      ))}
      {users.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">No users available to invite</p>
      )}
    </div>
  );
};

const CallOverlay = ({
  callState,
  onEndCall,
  onAcceptCall,
  onRejectCall,
  onCallConnected,
  callSignal,
  onCallSignalConsumed
}) => {
  const { user } = useAuth();
  const { sendWSEvent, callSignals, consumeCallSignal, clearAllCallSignals } = useChat();

  // Local state
  const [status, setStatus] = useState('idle'); // idle, ringing, connecting, connected
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [mediaError, setMediaError] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Multi-party state
  const [peers, setPeers] = useState({}); // { [userId]: { pc, stream, username } }
  const peersRef = useRef({});
  const localVideoRef = useRef(null);
  const timerRef = useRef(null);
  const localStreamRef = useRef(null);
  const iceCandidateQueuesRef = useRef({}); // { [userId]: [candidates] }

  // Extract from callState
  const isIncoming = callState?.isIncoming;
  const isVideo = callState?.isVideo;
  const callerName = callState?.callerName;
  const targetUserId = callState?.targetUserId;
  const isGroup = callState?.isGroup;
  const scopeId = callState?.scopeId;

  // ---- Create peer connection for a specific remote user ----
  const createPeerConnection = useCallback((remoteUserId, remoteUsername) => {
    console.log(`[Mesh] Creating peer connection to ${remoteUserId} (${remoteUsername})`);
    const pc = new RTCPeerConnection(ICE_SERVERS);

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendWSEvent({
          type: 'ice_candidate',
          target_user_id: remoteUserId,
          candidate: {
            candidate: event.candidate.candidate,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            sdpMid: event.candidate.sdpMid
          }
        });
      }
    };

    // Handle remote stream
    pc.ontrack = (event) => {
      console.log(`[Mesh] Got remote track from ${remoteUserId}`);
      const remoteStream = event.streams[0];
      setPeers(prev => ({
        ...prev,
        [remoteUserId]: { ...prev[remoteUserId], stream: remoteStream, username: remoteUsername }
      }));
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[Mesh] ICE state for ${remoteUserId}: ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setStatus('connected');
      }
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        // Clean up this peer
        removePeer(remoteUserId);
      }
    };

    peersRef.current[remoteUserId] = { pc, username: remoteUsername };
    setPeers(prev => ({ ...prev, [remoteUserId]: { pc, stream: null, username: remoteUsername } }));

    // Flush any queued ICE candidates
    const queue = iceCandidateQueuesRef.current[remoteUserId];
    if (queue?.length > 0) {
      queue.forEach(c => {
        pc.addIceCandidate(new RTCIceCandidate(c)).catch(err => console.warn('[Mesh] Queued ICE add error:', err));
      });
      iceCandidateQueuesRef.current[remoteUserId] = [];
    }

    return pc;
  }, [sendWSEvent]);

  const removePeer = useCallback((userId) => {
    const peer = peersRef.current[userId];
    if (peer?.pc) {
      peer.pc.close();
    }
    delete peersRef.current[userId];
    setPeers(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  }, []);

  // ---- Get local media ----
  const getLocalMedia = useCallback(async () => {
    try {
      const constraints = { audio: true, video: isVideo ? { width: 640, height: 480 } : false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      return stream;
    } catch (err) {
      console.error('[Mesh] Media error:', err);
      setMediaError('Could not access your camera or microphone. Please check browser permissions.');
      return null;
    }
  }, [isVideo]);

  // ---- Initiate outgoing call ----
  useEffect(() => {
    if (!callState || callState.isIncoming) return;
    if (status !== 'idle') return;

    const initiate = async () => {
      setStatus('ringing');
      const stream = await getLocalMedia();
      if (!stream) return;

      if (targetUserId && !isGroup) {
        // 1:1 call - create peer connection and send offer
        const pc = createPeerConnection(targetUserId, callerName);
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendWSEvent({
            type: 'call_offer',
            target_user_id: targetUserId,
            sdp: offer.sdp,
            sdp_type: offer.type,
            is_video: isVideo,
            caller_name: user?.display_name || user?.username || 'Unknown'
          });
          setStatus('connecting');
        } catch (err) {
          console.error('[Mesh] Offer error:', err);
          setMediaError('Failed to create call offer');
        }
      } else if (isGroup) {
        // Group call - register in call room, peers join via signaling
        setStatus('connecting');
        try {
          const res = await axios.post(`${API}/calls/start`, null, {
            params: { channel_id: scopeId, is_video: isVideo }
          });
          console.log('[Mesh] Call room started:', res.data);
          setStatus('connected');
          // Start timer immediately for group calls (we're in the room)
        } catch (err) {
          console.error('[Mesh] Start group call error:', err);
        }
      }
    };

    initiate();
  }, [callState]);

  // ---- Handle incoming call acceptance ----
  useEffect(() => {
    if (!callState || !callState.isIncoming) return;
    if (status !== 'idle') return;

    const handleIncoming = async () => {
      setStatus('ringing');
    };
    handleIncoming();
  }, [callState]);

  // When user accepts incoming call
  useEffect(() => {
    if (!callState?.isIncoming || status !== 'ringing') return;

    // The accept is signaled by onAcceptCall being called, which triggers callState updates
    // We listen for a flag or the offerSdp being set
    if (!callState.offerSdp) return;

    const accept = async () => {
      const stream = await getLocalMedia();
      if (!stream) return;
      setStatus('connecting');

      const callerId = callState.callerId;
      const pc = createPeerConnection(callerId, callerName);

      try {
        await pc.setRemoteDescription(new RTCSessionDescription({
          type: callState.sdpType || 'offer',
          sdp: callState.offerSdp
        }));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendWSEvent({
          type: 'call_answer',
          target_user_id: callerId,
          sdp: answer.sdp,
          sdp_type: answer.type
        });
        setStatus('connected');
        onCallConnected?.();
      } catch (err) {
        console.error('[Mesh] Accept error:', err);
        setMediaError('Failed to accept call');
      }
    };
    accept();
  }, [callState?.offerSdp]);

  // ---- Process signaling queue (multi-party) ----
  useEffect(() => {
    if (!callSignals || callSignals.length === 0) return;

    callSignals.forEach(signal => {
      const fromId = signal.from_user_id;
      if (!fromId) return;

      if (signal.type === 'call_answer') {
        const peer = peersRef.current[fromId];
        if (peer?.pc && peer.pc.signalingState === 'have-local-offer') {
          peer.pc.setRemoteDescription(new RTCSessionDescription({
            type: signal.sdp_type || 'answer',
            sdp: signal.sdp
          })).then(() => {
            console.log(`[Mesh] Set remote answer from ${fromId}`);
            setStatus('connected');
            onCallConnected?.();
          }).catch(err => console.error('[Mesh] Set answer error:', err));
        }
        consumeCallSignal(signal);
      } else if (signal.type === 'ice_candidate') {
        const peer = peersRef.current[fromId];
        if (peer?.pc && peer.pc.remoteDescription) {
          peer.pc.addIceCandidate(new RTCIceCandidate(signal.candidate))
            .catch(err => console.warn('[Mesh] ICE add error:', err));
        } else {
          // Queue the candidate
          if (!iceCandidateQueuesRef.current[fromId]) iceCandidateQueuesRef.current[fromId] = [];
          iceCandidateQueuesRef.current[fromId].push(signal.candidate);
        }
        consumeCallSignal(signal);
      } else if (signal.type === 'call_offer' && signal.is_mesh) {
        // New peer joining - create connection and answer
        const handleMeshOffer = async () => {
          if (!localStreamRef.current) await getLocalMedia();
          const pc = createPeerConnection(fromId, signal.from_username || 'Participant');
          try {
            await pc.setRemoteDescription(new RTCSessionDescription({
              type: signal.sdp_type || 'offer',
              sdp: signal.sdp
            }));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendWSEvent({
              type: 'call_answer',
              target_user_id: fromId,
              sdp: answer.sdp,
              sdp_type: answer.type
            });
          } catch (err) {
            console.error('[Mesh] Handle mesh offer error:', err);
          }
        };
        handleMeshOffer();
        consumeCallSignal(signal);
      } else if (signal.type === 'call_end') {
        removePeer(fromId);
        consumeCallSignal(signal);
        // If no peers left, end call
        if (Object.keys(peersRef.current).length === 0 && status === 'connected') {
          handleEndCall();
        }
      }
    });
  }, [callSignals]);

  // Also handle legacy single callSignal for backward compatibility
  useEffect(() => {
    if (!callSignal) return;
    // Already handled via callSignals queue, just consume
    onCallSignalConsumed?.();
  }, [callSignal]);

  // ---- Call duration timer ----
  useEffect(() => {
    if (status === 'connected') {
      timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status]);

  // ---- End call ----
  const handleEndCall = useCallback(() => {
    // Notify all peers
    Object.keys(peersRef.current).forEach(userId => {
      sendWSEvent({ type: 'call_end', target_user_id: userId });
    });

    // Close all peer connections
    Object.values(peersRef.current).forEach(p => p.pc?.close());
    peersRef.current = {};
    setPeers({});

    // Stop local media
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);

    // Leave call room if group
    if (scopeId && isGroup) {
      axios.post(`${API}/calls/active`).catch(() => {});
    }

    // Reset state
    if (timerRef.current) clearInterval(timerRef.current);
    setCallDuration(0);
    setStatus('idle');
    setIsMuted(false);
    setIsCameraOff(false);
    setMediaError(null);
    clearAllCallSignals();
    onEndCall?.();
  }, [sendWSEvent, onEndCall, clearAllCallSignals, scopeId, isGroup]);

  // ---- Invite user to join call (mesh) ----
  const inviteUser = useCallback(async (userId) => {
    if (!localStreamRef.current) return;
    const pc = createPeerConnection(userId, 'Participant');
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendWSEvent({
        type: 'call_offer',
        target_user_id: userId,
        sdp: offer.sdp,
        sdp_type: offer.type,
        is_video: isVideo,
        is_mesh: true,
        caller_name: user?.display_name || user?.username || 'Unknown'
      });
      toast.success('Invitation sent');
      setShowInviteDialog(false);
    } catch (err) {
      console.error('[Mesh] Invite error:', err);
      toast.error('Failed to invite user');
    }
  }, [createPeerConnection, sendWSEvent, isVideo, user]);

  // ---- Controls ----
  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(!isMuted);
    }
  };

  const toggleCamera = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsCameraOff(!isCameraOff);
    }
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      Object.values(peersRef.current).forEach(p => p.pc?.close());
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (!callState || status === 'idle') return null;

  const peerCount = Object.keys(peers).length;
  const existingPeerIds = Object.keys(peers);

  // ---- Media error state ----
  if (mediaError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="call-overlay">
        <div className="bg-card rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
            <PhoneOff className="w-8 h-8 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Media Access Error</h3>
          <p className="text-sm text-muted-foreground mb-4">{mediaError}</p>
          <p className="text-xs text-muted-foreground mb-6">Please allow camera and microphone access in your browser settings.</p>
          <Button variant="destructive" onClick={handleEndCall} data-testid="call-error-close-button">Close</Button>
        </div>
      </div>
    );
  }

  // ---- Incoming call ring UI ----
  if (status === 'ringing' && isIncoming) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="call-overlay">
        <div className="bg-card rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-3xl font-bold mx-auto mb-4 animate-pulse">
            {callerName?.[0] || '?'}
          </div>
          <h3 className="text-lg font-semibold mb-1" style={{fontFamily: 'Space Grotesk'}}>
            {callerName || 'Unknown'}
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Incoming {isVideo ? 'video' : 'audio'} call...
          </p>
          <div className="flex justify-center gap-6">
            <Button size="lg" variant="destructive" className="rounded-full w-14 h-14" onClick={() => { onRejectCall?.(); handleEndCall(); }} data-testid="call-reject-button">
              <PhoneOff className="w-6 h-6" />
            </Button>
            <Button size="lg" className="rounded-full w-14 h-14 bg-[hsl(var(--presence-online))] hover:bg-[hsl(var(--presence-online))]/90" onClick={() => { onAcceptCall?.(); }} data-testid="call-accept-button">
              <Phone className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Grid layout for video/audio ----
  const getGridCols = () => {
    const total = peerCount + 1; // +1 for local
    if (total <= 1) return 'grid-cols-1';
    if (total <= 2) return 'grid-cols-2';
    if (total <= 4) return 'grid-cols-2';
    if (total <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  // ---- Active call UI ----
  return (
    <div
      className={`fixed z-50 ${
        isMinimized ? 'bottom-4 right-4 w-80' : 'inset-0 flex flex-col bg-black/90'
      }`}
      data-testid="call-overlay"
    >
      {isMinimized ? (
        // ---- Minimized pill ----
        <div className="bg-card rounded-2xl shadow-2xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--presence-online))]/20 flex items-center justify-center">
              {isVideo ? <Video className="w-5 h-5 text-[hsl(var(--presence-online))]" /> : <Phone className="w-5 h-5 text-[hsl(var(--presence-online))]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{fontFamily: 'Space Grotesk'}}>
                {isGroup ? callState?.channelName || 'Group Call' : callerName || 'Call'}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formatDuration(callDuration)}</span>
                <Badge variant="secondary" className="text-[10px] h-4">{peerCount + 1} in call</Badge>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsMinimized(false)}>
                <Maximize2 className="w-4 h-4" />
              </Button>
              <Button variant="destructive" size="icon" className="h-8 w-8 rounded-full" onClick={handleEndCall} data-testid="call-end-button">
                <PhoneOff className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // ---- Full screen call UI ----
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 flex-shrink-0">
            <div className="flex items-center gap-3">
              <h3 className="text-white text-sm font-semibold" style={{fontFamily: 'Space Grotesk'}}>
                {isGroup ? `#${callState?.channelName || 'Group Call'}` : callerName || 'Call'}
              </h3>
              <Badge className="bg-white/10 text-white/80 border-0 text-[10px]">
                {peerCount + 1} participant{peerCount > 0 ? 's' : ''}
              </Badge>
              {status === 'connecting' && (
                <span className="text-xs text-white/50 animate-pulse">Connecting...</span>
              )}
              {status === 'ringing' && (
                <span className="text-xs text-white/50 animate-pulse">Ringing...</span>
              )}
              {status === 'connected' && (
                <span className="text-xs text-white/70">{formatDuration(callDuration)}</span>
              )}
            </div>
            <Button variant="ghost" size="icon" className="text-white/70 hover:text-white h-8 w-8" onClick={() => setIsMinimized(true)}>
              <Minimize2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Video / Audio Grid */}
          <div className="flex-1 px-4 pb-4 overflow-hidden">
            <div className={`grid ${getGridCols()} gap-2 h-full`}>
              {/* Local stream */}
              <div className="relative rounded-xl overflow-hidden bg-zinc-800 flex items-center justify-center" data-testid="local-video-tile">
                {isVideo ? (
                  <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary))]/30 flex items-center justify-center text-2xl font-bold text-white">
                      {user?.display_name?.[0] || user?.username?.[0] || '?'}
                    </div>
                    <span className="text-xs text-white/70">You</span>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                  <Badge className="bg-black/50 text-white border-0 text-[10px] h-5">You</Badge>
                  {isMuted && <Badge className="bg-red-500/80 text-white border-0 text-[10px] h-5"><MicOff className="w-2.5 h-2.5" /></Badge>}
                </div>
              </div>

              {/* Remote streams */}
              {Object.entries(peers).map(([userId, peerData]) => (
                <div key={userId} className="relative rounded-xl overflow-hidden bg-zinc-800 flex items-center justify-center" data-testid={`remote-tile-${userId}`}>
                  {isVideo && peerData.stream ? (
                    <RemoteVideo stream={peerData.stream} />
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 rounded-full bg-zinc-600 flex items-center justify-center text-2xl font-bold text-white">
                        {peerData.username?.[0] || '?'}
                      </div>
                      <span className="text-xs text-white/70">{peerData.username || 'Participant'}</span>
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2">
                    <Badge className="bg-black/50 text-white border-0 text-[10px] h-5">{peerData.username || 'Participant'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 py-5 flex-shrink-0" data-testid="call-controls">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isMuted ? 'destructive' : 'secondary'}
                  size="icon"
                  className="rounded-full w-12 h-12"
                  onClick={toggleMute}
                  data-testid="call-mute-button"
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isMuted ? 'Unmute' : 'Mute'}</TooltipContent>
            </Tooltip>

            {isVideo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isCameraOff ? 'destructive' : 'secondary'}
                    size="icon"
                    className="rounded-full w-12 h-12"
                    onClick={toggleCamera}
                    data-testid="call-camera-button"
                  >
                    {isCameraOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isCameraOff ? 'Camera On' : 'Camera Off'}</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="rounded-full w-12 h-12"
                  onClick={() => setShowInviteDialog(true)}
                  data-testid="call-add-participant-button"
                >
                  <UserPlus className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add Participant</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  size="icon"
                  className="rounded-full w-14 h-14"
                  onClick={handleEndCall}
                  data-testid="call-end-button"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>End Call</TooltipContent>
            </Tooltip>
          </div>
        </>
      )}

      {/* Add Participant Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-[360px]" data-testid="call-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Space Grotesk' }}>
              <UserPlus className="w-4 h-4" /> Add to Call
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[280px]">
            <InviteUserList
              currentUserId={user?.user_id}
              existingPeerIds={existingPeerIds}
              onInvite={inviteUser}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper component for remote video
const RemoteVideo = ({ stream }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
    }
  }, [stream]);
  return <video ref={ref} autoPlay playsInline className="w-full h-full object-cover" />;
};

export default CallOverlay;
